import { useMemo } from "react";
import { Control } from "react-hook-form";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import {
  PriorClaimSource,
  PriorClaimsQuestion,
} from "@covertree/new-business-graphql";
import { CloseButton, FormSelectField } from "@covertree/ui";
import {
  selectPersonalDetailsBasicFormDefaultValues,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import {
  PriorClaimsForm,
  PriorClaimsFormInput,
} from "@covertree/new-business-forms";

import { PriorClaimQuestion } from "./PriorClaimQuestion";

interface PriorClaimItemProps {
  questions: PriorClaimsQuestion[];
  index: number;
  claim: Partial<PriorClaimsFormInput>;
  onDelete: () => void;
  control: Control<PriorClaimsForm>;
  disabled?: boolean;
}

export function PriorClaimItem({
  questions,
  index,
  claim,
  onDelete,
  control,
  disabled,
}: PriorClaimItemProps) {
  const personalDetails = useNewBusinessSelector(
    selectPersonalDetailsBasicFormDefaultValues
  );

  const insuredPossibleValues = useMemo(() => {
    const { firstName, lastName } = personalDetails;
    if (!firstName || !lastName) return [];
    return [
      {
        label: `${firstName.trim()} ${lastName.trim()}`,
        value: `${firstName.trim()}|${lastName.trim()}`.toUpperCase(),
      },
    ];
  }, [personalDetails]);

  return (
    <Card
      sx={{
        display: "flex",
        flexGrow: 1,
        borderWidth: 1,
        backgroundColor: "background.default",
        position: "relative",
        flexDirection: "column",
      }}>
      {claim.source === PriorClaimSource.UserDeclared && (
        <CloseButton
          testId={`delete-claim-item-${index}`}
          onClick={onDelete}
          disabled={disabled}
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(3),
            right: theme.spacing(3),
          })}
        />
      )}
      <CardContent
        sx={{ display: "flex", flexGrow: 1, flexDirection: "column" }}>
        <Typography>Claim {index + 1}</Typography>
        <Box pt={4}>
          <Grid container spacing={3}>
            <Grid item lg={6} xs={12}>
              <FormSelectField
                id={`claimItem-${index}-insured`}
                name={`claims.${index}.insured`}
                control={control}
                options={insuredPossibleValues}
                label="Insured"
                disabled={
                  claim.source !== PriorClaimSource.UserDeclared || disabled
                }
              />
            </Grid>
            {questions.map((question) => (
              <PriorClaimQuestion
                key={`claim.${index}.question-${question.fieldName}`}
                question={question}
                claimIndex={index}
                claim={claim}
                disabled={disabled}
              />
            ))}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}
